import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDisableCandidateFormActions } from 'contexts/contexts';
import {
  belongsToCurrentPage,
  getErrorMessage,
  getSectionPrefixes,
  getSubsectionPrefixes,
  pickByPrefixes,
} from 'helpers/candidate_application/form-page-helpers';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { componentMap, getComponentProps, noHeaderComponents } from 'helpers/question-config';
import { useKeyboardHints } from 'hooks/keyboard-hints';
import { useQueryParams } from 'hooks/query-params';
import useAllFormValues from 'hooks/use-all-form-values';
import { useBeforeUnload } from 'hooks/use-before-unload';
import { useInitialProcessing } from 'hooks/use-initial-processing';
import { useOnChangeDependentProcessing } from 'hooks/use-process-dependent-felds';
import { AnimatePresence } from 'motion/react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import { Button } from 'components/FormComponents';
import { ValidationAlert } from 'components/FormComponents';
import { FormMessage } from 'components/FormComponents/FormMessage/FormMessage';
import ScaleMotion from 'components/Motion/ScaleMotion';
import SliderSideMotion from 'components/Motion/SliderSideMotion';
import { OrderNumber } from 'components/OrderNumber/OrderNumber';
import { StatusPill } from 'components/Pills/StatusPills';

import { selectCandidateApplication, updateFormValues } from 'api/candidate-application-slice';

// Component to render question with keyboard hints
const QuestionWithKeyboardHints = ({
  question,
  isVisible,
  fieldName,
  questionIndex,
  hideHeader,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  candidate,
  handlers,
  brand,
  fieldPathIndex,
  allValues,
  errors,
  checkValues,
}) => {
  const keyboardHint = useKeyboardHints(question?.type);

  // Announce field changes to screen readers
  useEffect(() => {
    if (error) {
      const announcement = `Error in ${question?.title || fieldName}: ${error}`;
      const announcementElement = document.getElementById('form-announcements');
      if (announcementElement) {
        announcementElement.textContent = announcement;
      }
    }
  }, [error, question?.title, fieldName]);

  return (
    <div
      role="group"
      aria-labelledby={question?.title ? `${fieldName}-label` : undefined}
      aria-describedby={`${fieldName}-instructions`}
      aria-invalid={!!error}
      aria-errormessage={error ? `${fieldName}-error` : undefined}>
      <QuestionWrapper
        question={question}
        isVisible={isVisible}
        componentMap={componentMap}
        questionIndex={questionIndex}
        control={control}
        register={register}
        setValue={setValue}
        trigger={trigger}
        getValues={getValues}
        error={error}
        requirementVariantStyle="text"
        gap={['multiple_choice', 'yes_no'].includes(question?.type) ? 'medium' : 'default'}
        titleSize="small"
        hideHeader={hideHeader}
        rejectedData={checkValues?.rejected_values}
        fieldName={fieldName}
        keyboardHint={keyboardHint}
        fieldPathIndex={fieldPathIndex}
        allValues={allValues}
        errors={errors}
        getComponentProps={value =>
          getComponentProps(
            question,
            value,
            candidate?.id,
            handlers,
            brand,
            false,
            fieldPathIndex,
            allValues,
          )
        }
      />
    </div>
  );
};

const FormPageRenderer = ({
  subsections,
  candidate,
  brand,
  handlers,
  fetched,
  sectionKey,
  singlePage = false,
  pageIndex,
  globalPageIndex,
  totalPages,
  primaryActionLabel = 'Continue',
  secondaryActionLabel = 'Cancel',
  customError = '',
  onFormSubmit = () => {},
  children,
}) => {
  const { t } = useTranslation();
  // Router hooks
  const navigate = useNavigate();
  const { token } = useParams();
  const [admin] = useQueryParams(['admin']);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = useCallback(path => buildNavPathHelper(path, admin), [admin]);

  // Redux hooks
  const dispatch = useDispatch();
  const {
    application: { check_values },
    candidateFields,
  } = useSelector(selectCandidateApplication);

  // Context to check if form actions should be disabled
  const { disabled: isFormActionsDisabled, disableActions } = useDisableCandidateFormActions();

  // Form hooks
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    unregister,
    watch,
    setError,
    clearErrors,
    formState: { errors, isValid, isDirty, touchedFields, dirtyFields },
    control,
    setValue,
    trigger,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // State hooks
  const [isBackward, setIsBackward] = useState(false);
  const [focusManagement, setFocusManagement] = useState({ shouldFocus: false, targetId: null });
  const [customErrorMessage, setCustomErrorMessage] = useState(customError);

  useEffect(() => {
    setCustomErrorMessage(customError);
  }, [customError]);

  // Refs
  const prevVisibility = useRef({});
  const formRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Extract Redux data
  const {
    sections = [],
    formValues = {},
    sectionFieldPrefixes = {},
    fieldPathIndex = {},
    wrapperValidators = {},
  } = candidateFields || {};

  const currentSection = sections?.find(s => s.code === sectionKey);

  const hasUnsavedChanges = isDirty; // from react-hook-form

  useBeforeUnload(hasUnsavedChanges, () => console.log('Leaving without submitting (browser)'));

  // Helper function for conditional field evaluation
  const evaluateFieldVisibility = (field, allFormValues, parentField = null, arrayIndex = 0) => {
    // 1) field-level conditions
    const fieldPasses = evaluateFieldConditions(field?.conditions, allFormValues || {}, {
      parentField,
      arrayIndex,
      fieldPathIndex,
    });
    if (!fieldPasses) return false;

    // 2) if this is an `object` or `id_requirements` wrapper, require at least one visible subField
    if (
      (field?.type === 'object' || field?.type === 'id_requirements') &&
      Array.isArray(field?.subFields) &&
      field.subFields.length > 0
    ) {
      const anySubVisible = field.subFields.some(subField =>
        evaluateFieldConditions(subField?.conditions, allFormValues || {}, {
          parentCode: field?.code,
          fieldPathIndex,
        }),
      );
      return anySubVisible;
    }
    return true;
  };

  // Memoized values
  const subsectionsToRender = useMemo(() => {
    let filteredSubsections = (subsections || [])?.filter(subsection => {
      if (!subsection?.fields || subsection?.fields?.length === 0) {
        return false;
      }

      return true;
    });

    // For multi-page sections, only show the current page
    if (!singlePage && pageIndex !== undefined) {
      const currentPageIndex = parseInt(pageIndex) || 0;
      const currentSubsection = filteredSubsections?.[currentPageIndex];
      return currentSubsection ? [currentSubsection] : [];
    }

    return filteredSubsections;
  }, [subsections, singlePage, pageIndex]);

  const currentSubsectionCode = useMemo(() => {
    if (singlePage) return null;
    const subsection = Array.isArray(subsectionsToRender) ? subsectionsToRender[0] : null;
    return subsection?.code || null;
  }, [singlePage, subsectionsToRender]);

  const currentPagePrefixes = useMemo(() => {
    return singlePage
      ? getSectionPrefixes(sectionKey, sectionFieldPrefixes)
      : getSubsectionPrefixes(sectionKey, currentSubsectionCode, sectionFieldPrefixes);
  }, [singlePage, sectionKey, currentSubsectionCode, sectionFieldPrefixes]);

  // Values hooks
  const watchValues = useWatch({ control });

  // Filter watchValues to only include current page values
  const currentPageWatchValues = useMemo(() => {
    if (!watchValues || Object.keys(watchValues).length === 0) {
      return getValues();
    }
    return pickByPrefixes(watchValues, currentPagePrefixes);
  }, [watchValues, currentPagePrefixes, getValues]);

  const allValues = useAllFormValues({
    watchValues: currentPageWatchValues,
    currentPagePrefixes,
  });

  useEffect(() => {
    if (customErrorMessage) {
      setCustomErrorMessage('');
    }
  }, [currentPageWatchValues]);

  // Register wrapper fields for validation
  useEffect(() => {
    if (!wrapperValidators || Object.keys(wrapperValidators).length === 0) return;

    // Register wrapper fields that have validators
    Object.keys(wrapperValidators).forEach(wrapperKey => {
      if (belongsToCurrentPage(wrapperKey, currentPagePrefixes)) {
        register(wrapperKey);
      }
    });

    // Cleanup: unregister wrapper fields when component unmounts or section changes
    return () => {
      Object.keys(wrapperValidators).forEach(wrapperKey => {
        if (belongsToCurrentPage(wrapperKey, currentPagePrefixes)) {
          unregister(wrapperKey);
        }
      });
    };
  }, [wrapperValidators, currentPagePrefixes, register, unregister]);

  // Custom hooks
  useInitialProcessing({
    fetched,
    sectionKey,
    currentPagePrefixes,
    reset,
    getValues,
    trigger,
    setError,
    clearErrors,
    check_values,
    control,
  });

  useOnChangeDependentProcessing(
    watch,
    allValues,
    currentPagePrefixes,
    trigger,
    setError,
    clearErrors,
  );

  // Focus management effect
  useEffect(() => {
    if (focusManagement.shouldFocus && focusManagement.targetId) {
      const targetElement = document.getElementById(focusManagement.targetId);
      if (targetElement) {
        targetElement.focus();
        setFocusManagement({ shouldFocus: false, targetId: null });
      }
    }
  }, [focusManagement]);

  // Focus on form title when section changes
  useEffect(() => {
    // Focus on form title when sectionKey changes (navigating between different sections)
    setTimeout(() => {
      setFocusManagement({ shouldFocus: true, targetId: 'form-title' });
    }, 100);
  }, [sectionKey]);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = event => {
      // Skip if user is typing in an input field
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT'
      ) {
        return;
      }

      switch (event.key) {
        case 'Home':
          event.preventDefault();
          if (firstFieldRef.current) {
            firstFieldRef.current.focus();
          }
          break;
        case 'End':
          event.preventDefault();
          const lastButton = formRef.current?.querySelector('button[type="submit"]');
          if (lastButton) {
            lastButton.focus();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Effects
  // Update form values when page changes
  useEffect(() => {
    if (!watchValues || Object.keys(watchValues).length === 0) return;
    const sectionScoped = {
      ...formValues[sectionKey],
      ...pickByPrefixes(watchValues, currentPagePrefixes),
    };
    if (Object.keys(sectionScoped).length) {
      dispatch(updateFormValues({ sectionKey, formValues: sectionScoped }));
    }
  }, [watchValues, sectionKey, currentPagePrefixes, dispatch]);

  // Clear validation errors from other pages when page changes
  useEffect(() => {
    if (!subsections || subsections.length === 0) return;
    Object.keys(errors).forEach(fieldName => {
      if (!belongsToCurrentPage(fieldName, currentPagePrefixes)) {
        // Clear errors for fields that don't belong to current page
        clearErrors(fieldName);
      }
    });
  }, [sectionKey, currentPagePrefixes, errors, clearErrors, subsections]);

  useEffect(() => {
    if (!watchValues || Object.keys(watchValues).length === 0) return;

    (subsections || [])
      .flatMap(g => g?.fields || [])
      .forEach(field => {
        if (!field?.code) return;
        const isVisible = evaluateFieldVisibility(field, allValues, null, 0);
        if (prevVisibility.current[field.code] && !isVisible) {
          const current = getValues();
          Object.keys(current).forEach(k => {
            if (k === field.code || k.startsWith(`${field.code}.`)) {
              unregister(k, { keepValue: false });
            }
          });
        }
        prevVisibility.current[field.code] = isVisible;
      });
  }, [watchValues, unregister, getValues, subsections, fieldPathIndex]);

  // Callbacks
  const navigateToNextPage = useCallback(() => {
    setIsBackward(false);
    const currentPage = parseInt(globalPageIndex) || 0;
    const nextPage = currentPage + 1;

    if (nextPage < totalPages) {
      // Navigate to next page
      navigate(buildNavPath(`/form_submission/candidate/${sectionKey}/${token}/page/${nextPage}`));
    } else {
      // No more pages, go back to dashboard
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
    }

    // Focus management for navigation
    setTimeout(() => {
      setFocusManagement({ shouldFocus: true, targetId: 'form-title' });
    }, 100);
  }, [navigate, globalPageIndex, totalPages, sectionKey, token]);

  const navigateToPreviousPage = useCallback(() => {
    setIsBackward(true);
    const currentPage = parseInt(globalPageIndex) || 0;
    const previousPage = currentPage - 1;
    setCustomErrorMessage('');

    if (previousPage >= 0) {
      // Navigate to previous page
      navigate(
        buildNavPath(`/form_submission/candidate/${sectionKey}/${token}/page/${previousPage}`),
      );
    } else {
      // Go back to dashboard or index page
      const section = sections?.find(s => s.code === sectionKey);
      if (section?.indexPage) {
        navigate(buildNavPath(`/form_submission/candidate/${sectionKey}/${token}`));
      } else {
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      }
    }

    // Focus management for navigation
    setTimeout(() => {
      setFocusManagement({ shouldFocus: true, targetId: 'form-title' });
    }, 100);
  }, [navigate, globalPageIndex, sectionKey, token, sections]);

  const handleFormSubmission = useCallback(
    async (temp_Save = false, rejectedValuesFix = false) => {
      const customValidationResult = await onFormSubmit(
        currentPageWatchValues,
        {
          subsections: subsectionsToRender,
          currentPagePrefixes,
          initialValues: getValues(),
        },
        temp_Save,
        rejectedValuesFix,
      );

      if (customValidationResult === false) {
        return false; // Stop if custom validation fails and return false to prevent navigation
      }

      if (sectionKey === 'consents') {
        return true;
      }

      // If all validations pass, handle navigation
      if (singlePage) {
        // Single page section - go back to dashboard
        setIsBackward(false);
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      } else {
        // Multi-page section - go to next page or dashboard
        navigateToNextPage();
      }

      return true; // Indicate successful submission
    },
    [
      onFormSubmit,
      currentPageWatchValues,
      singlePage,
      navigate,
      token,
      navigateToNextPage,
      subsectionsToRender,
      sectionKey,
      currentPagePrefixes,
    ],
  );

  const onSubmit = useCallback(
    async e => {
      // Prevent default form submission behavior
      if (e && e.preventDefault) {
        e.preventDefault();
      }

      // IMPORTANT: Get the latest errors from form state right before submission
      // React Hook Form's handleSubmit validates before calling onSubmit, so we need fresh errors
      const currentErrors = control._formState?.errors || errors;

      // Check for wrapper validation errors before submission
      const wrapperErrorKeys = Object.keys(currentErrors || {}).filter(
        key => wrapperValidators?.[key],
      );

      // Also check if any wrapper errors have actual error messages (not just empty objects)
      const wrapperErrors = (wrapperErrorKeys || [])?.filter(key => {
        const error = currentErrors[key];
        return error && (error.message || typeof error === 'string');
      });

      if (wrapperErrors.length > 0) {
        return false; // Prevent submission
      }

      return await handleFormSubmission(false, check_values?.rejected);
    },
    [handleFormSubmission, errors, wrapperValidators, control, trigger],
  );

  const onError = useCallback(
    errors => {
      // Clear cross-section validation errors immediately
      Object.keys(errors).forEach(fieldName => {
        if (!belongsToCurrentPage(fieldName, currentPagePrefixes)) {
          clearErrors(fieldName);
        }
      });

      // Announce validation errors to screen readers
      const errorCount = Object.keys(errors).length;
      if (errorCount > 0) {
        const announcement = `Form has ${errorCount} validation error${errorCount > 1 ? 's' : ''}. Please review the highlighted fields.`;
        const announcementElement = document.getElementById('form-announcements');
        if (announcementElement) {
          announcementElement.textContent = announcement;
        }
      }
    },
    [currentPagePrefixes, clearErrors],
  );

  const handleSaveAndExit = useCallback(async () => {
    try {
      // Check for validation issues before saving
      const hasValidationIssues = !isValid || Object.keys(errors).length > 0;

      // Call custom submit handler with temp_Save = true, bypassing validation
      // Pass validation status to the parent component
      const result = await onFormSubmit(
        currentPageWatchValues,
        {
          subsections: subsectionsToRender,
          currentPagePrefixes,
          hasValidationIssues, // Pass validation status to parent
        },
        true,
      );

      if (result !== false) {
        // Navigate back to dashboard after successful save
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      }
    } catch (error) {
      console.error('Error saving form:', error);
    }
  }, [
    onFormSubmit,
    currentPageWatchValues,
    subsectionsToRender,
    currentPagePrefixes,
    navigate,
    token,
    isValid,
  ]);

  const showHeader = useCallback(type => {
    return noHeaderComponents.includes(type);
  }, []);

  // Early returns
  if (!fetched) {
    return (
      <div role="status" aria-live="polite">
        <p>Loading form...</p>
      </div>
    );
  }

  if (!getValues || typeof getValues !== 'function') {
    return (
      <div role="status" aria-live="polite">
        <p>Form is initializing...</p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={async e => {
        e.preventDefault(); // Prevent default form submission

        // Check for wrapper errors BEFORE React Hook Form validation runs
        // This prevents handleSubmit from clearing manual errors
        const currentErrors = control._formState?.errors || errors;
        const wrapperErrorKeys = Object.keys(currentErrors || {}).filter(
          key => wrapperValidators?.[key],
        );
        const wrapperErrors = (wrapperErrorKeys || [])?.filter(key => {
          const error = currentErrors[key];
          return error && (error.message || typeof error === 'string');
        });

        if (wrapperErrors.length > 0) {
          console.error('Form submission blocked - wrapper errors detected:', wrapperErrors);
          // Call onError to show errors to user
          onError(currentErrors);
          return;
        }

        // No wrapper errors, proceed with normal validation
        handleSubmit(onSubmit, onError)(e);
      }}
      role="main"
      aria-labelledby="form-title"
      aria-describedby="form-description">
      {/* Skip link for keyboard navigation */}
      <a
        href="#form-content"
        className="sr-only u-focus-visible u-margin-bottom"
        onClick={e => {
          e.preventDefault();
          const target = document.getElementById('form-content');
          if (target) {
            target.focus();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        onFocus={e => e.target.classList.remove('sr-only')}
        onBlur={e => e.target.classList.add('sr-only')}>
        Go to form content
      </a>

      {/* Page indicator for multi-page sections */}
      {!singlePage && pageIndex !== undefined && (
        <div
          className="position-fixed-bottom-right u-border-radius u-border surface-secondary u-drop-shadow--xs u-padding--small"
          role="status"
          aria-live="polite"
          aria-label="Page progress">
          <div className="d-flex flex-row u-gap-small align-items-center">
            <span className="secondary-text u-font-size-small">
              Page {parseInt(globalPageIndex) + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
      {sectionKey !== 'consents' && (
        <Button
          type="secondary"
          className="action-top-right-fixed u-drop-shadow--xs"
          onClick={handleSaveAndExit}
          aria-label="Save form and exit to dashboard">
          {t('common.saveAndExit')}
        </Button>
      )}
      <AnimatePresence mode="wait">
        <SliderSideMotion
          key={globalPageIndex}
          isBackward={isBackward}
          delay={0.1}
          className="d-flex flex-column u-gap-large">
          {/* Form title and description for screen readers */}
          <div id="form-title" className="sr-only u-focus-visible" tabIndex="-1">
            {currentSection?.title || t('common.formSection')}
          </div>
          <div id="form-description" className="sr-only">
            {currentSection?.description ||
              t('common.pleaseCompleteAllRequiredFieldsInThisFormSection')}
          </div>

          {/* Screen reader announcements */}
          <div
            id="form-announcements"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            role="status"></div>
          <div
            id="form-content"
            className="d-flex flex-column u-gap-xx-large candidate-form-page"
            tabIndex="-1">
            {subsectionsToRender?.map((subsection, subsectionIndex) => {
              // Calculate visibility for all fields in this subsection once

              const fieldVisibility =
                subsection?.fields?.map(field => ({
                  field,
                  isVisible: evaluateFieldVisibility(field, allValues),
                })) || [];

              // Check if any fields in this subsection are visible
              const hasVisibleFields = fieldVisibility.some(({ isVisible }) => isVisible);

              if (!hasVisibleFields) {
                return null;
              }

              return (
                <section
                  key={`subsection-${subsection.code}-${subsectionIndex}`}
                  className={`${hasVisibleFields ? 'd-flex flex-column u-gap-medium ' : 'd-hide'}`}
                  aria-labelledby={`subsection-${subsection.code}-${subsectionIndex}-title`}
                  role="group">
                  <div className="group-header d-flex flex-column">
                    {singlePage && <OrderNumber orderNumber={subsectionIndex + 1} />}
                    <h2
                      id={`subsection-${subsection.code}-${subsectionIndex}-title`}
                      className="sr-only">
                      {t('common.section')} {subsectionIndex + 1}:{' '}
                      {subsection?.title || t('common.formFields')}
                    </h2>
                  </div>
                  {(fieldVisibility || [])?.map(({ field, isVisible }, fieldIndex) => {
                    return (
                      <fieldset
                        key={`${subsection.code}-${field.code}-${fieldIndex}`}
                        className={isVisible ? 'd-block' : 'd-hide'}
                        aria-hidden={!isVisible}>
                        {field?.type && !noHeaderComponents.includes(field?.type) && (
                          <div className="d-flex flex-column u-gap-small u-margin-bottom">
                            {field?.hasIdRequirements === false && (
                              <StatusPill text={t('common.optional')} status={'light'} />
                            )}
                            <div className="d-flex flex-row u-gap-medium align-items-center">
                              <legend className="primary-text u-font-size-xx-large u-font-weight-500 u-margin-0 u-border-none">
                                {field?.title}
                                {field?.required && (
                                  <span
                                    className="u-color-error"
                                    aria-label="required"
                                    style={{ color: '#d32f2f' }}>
                                    {' '}
                                    *
                                  </span>
                                )}
                              </legend>
                            </div>
                            {field?.description && (
                              <div className="secondary-text u-font-size-small u-font-weight-400">
                                {field?.description}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Field instructions for screen readers */}
                        <div className="sr-only" id={`${field.code}-instructions`}>
                          {field?.required ? 'Required field. ' : 'Optional field. '}
                          {field?.type === 'email' && 'Enter a valid email address. '}
                          {field?.type === 'phone' && 'Enter a valid phone number. '}
                          {field?.type === 'date' && 'Enter date in the format shown. '}
                          {field?.min && `Minimum value: ${field.min}. `}
                          {field?.max && `Maximum value: ${field.max}. `}
                          {field?.minLength && `Minimum length: ${field.minLength} characters. `}
                          {field?.maxLength && `Maximum length: ${field.maxLength} characters. `}
                        </div>
                        {(() => {
                          const renderQuestion = (
                            question,
                            fieldName,
                            questionIndex = null,
                            hideHeader = false,
                          ) => {
                            return (
                              <QuestionWithKeyboardHints
                                question={question}
                                isVisible={isVisible}
                                fieldName={fieldName}
                                questionIndex={questionIndex}
                                hideHeader={hideHeader}
                                control={control}
                                register={register}
                                setValue={setValue}
                                trigger={trigger}
                                getValues={getValues}
                                error={(() => {
                                  // Check if field is registered
                                  const isRegistered = control._fields[fieldName] !== undefined;
                                  const error = getErrorMessage(errors[fieldName]);
                                  return error;
                                })()}
                                componentMap={componentMap}
                                candidate={candidate}
                                handlers={handlers}
                                brand={brand}
                                fieldPathIndex={fieldPathIndex}
                                allValues={allValues}
                                errors={errors}
                                checkValues={check_values}
                              />
                            );
                          };

                          if (field.type === 'dynamic_array' || field.type === 'id_requirements') {
                            return renderQuestion(field, field.code, null, true);
                          } else if (field.type === 'array') {
                            return renderQuestion(field, field.code, null, true);
                          } else if (field?.subFields && field.subFields.length > 0) {
                            // Check if wrapper has a validator error
                            const wrapperError = errors?.[field.code];
                            const hasWrapperError =
                              wrapperError &&
                              (typeof wrapperError === 'string' || wrapperError?.message);

                            return (
                              <div className="d-flex flex-column u-gap-large">
                                {/* Display wrapper error if it exists */}
                                {hasWrapperError && (
                                  <div className="u-margin-bottom--small">
                                    <ValidationAlert
                                      show={true}
                                      message={
                                        typeof wrapperError === 'string'
                                          ? wrapperError
                                          : wrapperError?.message
                                      }
                                    />
                                  </div>
                                )}
                                {field?.subFields?.map((subField, subFieldIndex) => {
                                  // Check if subfield has conditions
                                  let conditionsMet = true;
                                  if (subField?.conditions) {
                                    conditionsMet = evaluateFieldConditions(
                                      subField.conditions,
                                      allValues || {},
                                      {
                                        parentCode: field?.code,
                                        arrayIndex: subFieldIndex,
                                        fieldPathIndex,
                                      },
                                    );
                                  }

                                  // Skip rendering if conditions aren't met
                                  if (!conditionsMet) {
                                    return null;
                                  }

                                  const question = { ...subField, text: subField?.title };
                                  return (
                                    <div key={`${field.code}-${subField.code}-${subFieldIndex}`}>
                                      {renderQuestion(
                                        question,
                                        subField.code,
                                        subFieldIndex,
                                        showHeader(question?.type),
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          } else {
                            return renderQuestion(field, field.code, null, true);
                          }
                        })()}
                      </fieldset>
                    );
                  })}
                </section>
              );
            })}
          </div>
          {children}
          <div role="alert" aria-live="assertive">
            {customErrorMessage && (
              <FormMessage
                message={customErrorMessage}
                type="Error"
                noMargin
                closeable={false}
                aria-label="Form error message"
              />
            )}
          </div>
          {isFormActionsDisabled && (
            <div role="alert" aria-live="assertive">
              <FormMessage
                message={t(
                  'common.pleaseCheckAndConfirmTheDetailsOfTheUploadedDocumentsAboveBeforeContinuing',
                )}
                type="Info"
                noMargin
                closeable={false}
                aria-label="Uploaded documents message"
              />
            </div>
          )}
          <div className="d-flex flex-row u-gap-medium" role="group" aria-label="Form navigation">
            <ScaleMotion keyProp="secondary-button" scale={1.05}>
              {!singlePage && parseInt(globalPageIndex) > 0 ? (
                <>
                  <CustomButton
                    onClick={navigateToPreviousPage}
                    brand={brand}
                    marginTop={false}
                    className="u-font-weight-400 md-hide"
                    icon="arrow-left"
                    width="auto"
                    onlyIcon={true}
                    rounded={true}
                    aria-label="Go to previous page"
                  />
                  <Button
                    type="secondary"
                    className="sm-hide"
                    onClick={navigateToPreviousPage}
                    aria-label="Go to previous page">
                    Previous
                  </Button>
                </>
              ) : (
                <Button
                  type="secondary"
                  onClick={() =>
                    navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`))
                  }
                  aria-label="Cancel and return to dashboard">
                  {secondaryActionLabel}
                </Button>
              )}
            </ScaleMotion>
            <ScaleMotion keyProp="primary-button" scale={1.05}>
              {check_values?.rejected ? (
                <CustomButton
                  type="primary"
                  brand={brand}
                  submit
                  icon="arrow-right"
                  prependIcon={false}
                  disabled={isFormActionsDisabled || disableActions}
                  aria-label="Update and submit">
                  {t('common.updateAndSubmit')}
                </CustomButton>
              ) : (
                <CustomButton
                  type="primary"
                  brand={brand}
                  submit
                  icon="arrow-right"
                  prependIcon={false}
                  disabled={isFormActionsDisabled || disableActions}
                  aria-label={
                    singlePage
                      ? `Submit ${primaryActionLabel}`
                      : (parseInt(globalPageIndex) || 0) >= totalPages - 1
                        ? 'Complete form section'
                        : `Continue to next page: ${primaryActionLabel}`
                  }>
                  {singlePage
                    ? primaryActionLabel
                    : (parseInt(globalPageIndex) || 0) >= totalPages - 1
                      ? t('common.complete')
                      : primaryActionLabel}
                </CustomButton>
              )}
            </ScaleMotion>
          </div>
        </SliderSideMotion>
      </AnimatePresence>
    </form>
  );
};

export default FormPageRenderer;
